import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { RootState } from "./store";
import { KycDocumentGroup, GovernmentID } from "@/schemas/kyc";

export type DialogType =
  | "kycDetails"
  | "kycStandardLevel"
  | "kycPlatinumLevel"
  | "addWithdrawalAccount"
  | "addSendMoneyAccount"
  | null;

export type LockReason = "proc360_repeat" | "non_proc360";

export interface KycLock {
  lockedUntil: number; // Timestamp in ms
  reason: LockReason;
}

export interface KycErrorRecord {
  source?: string;
  message: string;
}

interface ProfileState {
  userId: string;
  dialog: DialogType;
  kycNextLevelDocuments?: KycDocumentGroup;
  kycLocks: Record<string, KycLock>; 
  kycErrors: Record<string, KycErrorRecord>;
}

const initialState: ProfileState = {
  userId: "",
  dialog: null,
  kycLocks: {},
  kycErrors: {},
};

const PROC360_SOURCE = "proc360";
const LOCK_DURATION_MS = 20 * 60 * 1000;

const profileSlice = createSlice({
  name: "kyc",
  initialState,
  reducers: {
    setUserId(state, action: PayloadAction<string>) {
      state.userId = action.payload;
    },

    setKycNextLevelRequirements(
      state,
      action: PayloadAction<KycDocumentGroup | undefined>,
    ) {
      state.kycNextLevelDocuments = action.payload;
    },

    openDialog(state, action: PayloadAction<DialogType>) {
      state.dialog = action.payload;
    },

    closeDialog(state) {
      state.dialog = null;
    },

    openKycStandardLevelDialog(state) {
      state.dialog = "kycStandardLevel";
    },

    openKycPlatinumLevelDialog(state) {
      state.dialog = "kycPlatinumLevel";
    },

    openKycDetailsialog(state) {
      state.dialog = "kycDetails";
    },

    openAddWithdrawalAccountDialog(state) {
      state.dialog = "addWithdrawalAccount";
    },

    openAddSendMoneyAccountDialog(state) {
      state.dialog = "addSendMoneyAccount";
    },

    /**
     *  - error.source !== Proc360        -> lock immediately
     *  - error.source === Proc360:
     *      - same message as last attempt -> lock
     *      - different message            -> save it, allow retry
     */
    recordKycAttemptError(
      state,
      action: PayloadAction<{
        documentType: GovernmentID;
        source?: string;
        message: string;
      }>,
    ) {
      const { documentType, source, message } = action.payload;
      const isProc360 = source?.toLowerCase() === PROC360_SOURCE;
      const prev = state.kycErrors[documentType];

      if (!isProc360) {
        const isRepeatOfLastGeneric =
          prev?.source?.toLowerCase() !== PROC360_SOURCE && prev?.message === message;

        if (isRepeatOfLastGeneric) {
          state.kycLocks[documentType] = {
            lockedUntil: Date.now() + LOCK_DURATION_MS,
            reason: "non_proc360",
          };
        } else {
          state.kycErrors[documentType] = { source, message };
          delete state.kycLocks[documentType];
        }
        return;
      }

      const isRepeatOfLast =
        prev?.source?.toLowerCase() === PROC360_SOURCE && prev.message === message;

      if (isRepeatOfLast) {
        state.kycLocks[documentType] = {
          lockedUntil: Date.now() + LOCK_DURATION_MS,
          reason: "proc360_repeat",
        };
      } else {
        state.kycErrors[documentType] = { source, message };
        delete state.kycLocks[documentType];
      }
    },

    /**
     * Clears saved error/lock state for a document type, e.g. after a
     * successful upgrade.
     */
    clearKycAttemptState(
      state,
      action: PayloadAction<{ documentType: GovernmentID }>,
    ) {
      delete state.kycErrors[action.payload.documentType];
      delete state.kycLocks[action.payload.documentType];
    },

    /**
     * Optional utility to clear out specific expired locks manually if needed
     */
    clearExpiredLocks(state) {
      const now = Date.now();
      Object.keys(state.kycLocks).forEach((key) => {
        if (state.kycLocks[key].lockedUntil <= now) {
          delete state.kycLocks[key];
        }
      });
    },
  },
});

// Base Selectors
export const selectProfileState = (state: RootState) => state.profile;

export const selectCurrentDialog = createSelector(
  [selectProfileState],
  (state) => state.dialog,
);

export const selectUserId = createSelector(
  [selectProfileState],
  (state) => state.userId,
);

export const selectKycNextLevelRequirements = createSelector(
  [selectProfileState],
  (state) => state.kycNextLevelDocuments,
);

export const selectKycLocks = createSelector(
  [selectProfileState],
  (state) => state.kycLocks,
);

export const selectKycErrors = createSelector(
  [selectProfileState],
  (state) => state.kycErrors,
);

// Boolean Dialog Selectors
export const selectIsDialogOpen = createSelector(
  [selectCurrentDialog],
  (dialog) => dialog !== null,
);

export const selectIsKycStandardLevelDialogOpen = createSelector(
  [selectCurrentDialog],
  (dialog) => dialog === "kycStandardLevel",
);

export const selectIsKycPlatinumLevelDialogOpen = createSelector(
  [selectCurrentDialog],
  (dialog) => dialog === "kycPlatinumLevel",
);

export const selectIsKycDetailsDialogOpen = createSelector(
  [selectCurrentDialog],
  (dialog) => dialog === "kycDetails",
);

export const selectIsAddWithdrawalAccountDialogOpen = createSelector(
  [selectCurrentDialog],
  (dialog) => dialog === "addWithdrawalAccount",
);

export const selectIsAddSendMoneyAccountDialogOpen = createSelector(
  [selectCurrentDialog],
  (dialog) => dialog === "addSendMoneyAccount",
);

export const {
  openDialog,
  closeDialog,
  setUserId,
  setKycNextLevelRequirements,
  openKycDetailsialog,
  openAddWithdrawalAccountDialog,
  openKycPlatinumLevelDialog,
  openKycStandardLevelDialog,
  openAddSendMoneyAccountDialog,
  recordKycAttemptError,
  clearKycAttemptState,
  clearExpiredLocks,
} = profileSlice.actions;

export default profileSlice.reducer;